import Descriptor from '@r6digital/rapidstor-types/react/entities/Descriptor';
import Location from '@r6digital/rapidstor-types/react/entities/Location';
import UnitType from '@r6digital/rapidstor-types/react/entities/UnitType';
import { DealSelectorVariant } from '@r6digital/rapidstor-types/react/entities/Journey';

import { ReservationData } from '@/store/bookingSlice';
import { sortUnitTypes } from '@/features/rapidstor/helpers';
import { DescriptorOptions } from '@/features/rapidstor/UnitOptions';

interface PrepareBookingResult {
    reservationData: ReservationData;
    upsellDescriptor: Descriptor | null;
}

/**
 * Prepares the store data needed before navigating to customise-booking.
 * Extracts the common logic from book-unit's onContinueEvent so it can be
 * shared between unit-listing (when usingDealSelectorVariant skips book-unit)
 * and the book-unit-guard (for deep-link handling).
 */
export function prepareBookingNavigation(
    descriptor: Descriptor,
    descriptors: Descriptor[],
    location: Location,
    currentReservationData?: ReservationData,
    dealSelectorVariant?: DealSelectorVariant,
): PrepareBookingResult | null {
    const sortedUnitTypes = sortUnitTypes(descriptor.unitTypes, location);
    if (sortedUnitTypes.length === 0) {
        return null;
    }
    const unitType: UnitType = sortedUnitTypes.find(
        (a) => a.totalAvailable > 0 && a.units.length,
    ) ?? sortedUnitTypes[0];

    // Compute upsell descriptor
    const options = DescriptorOptions(descriptor, descriptors, location);
    const upsellDescriptor = options.nextSizeUp(descriptors, descriptor.area);

    // When usingDealSelectorVariant is on, pre-select the first available deal,
    // preserving the existing deal selection if it still applies to this unit type.
    let deal: string = '';
    let dealObject: ReservationData['dealObject'];

    if (dealSelectorVariant) {
        if (unitType.deals && unitType.deals.length > 0) {
            const existingDeal = unitType.deals.find((d) => d._id === currentReservationData?.deal);
            const selectedDeal = existingDeal ?? unitType.deals[0];
            dealObject = selectedDeal;
            deal = selectedDeal._id as string;
        }
    } else {
        deal = (unitType.deal?._id as string) ?? '';
        dealObject = unitType.deal ?? undefined;
    }

    const reservationData: ReservationData = {
        unitType,
        descriptor,
        deal,
        dealObject,
    };

    if (currentReservationData?.token) {
        reservationData.token = currentReservationData.token;
    }

    return { reservationData, upsellDescriptor };
}
